/**
 * Client profile contact form — Zod contract for ClientContactFormModal.
 * Maps to `Contact` (medplum-ui) then to FHIR via `contactFormToPatientContact` /
 * `contactFormToRelatedPerson` in `clientProfileFhir.ts`. Medplum persists FHIR; there is no separate REST API.
 */
import { z } from 'zod';
import {
  CONTACT_KINDS,
  CONTACT_RELATIONSHIP_TYPES,
  type Contact,
  type ContactKind,
  type ContactRelationshipType,
  type ContactSource,
} from '@/pages/clients/data/contactTypes';

export const clientContactPhoneTypeValues = ['Home', 'Work', 'Mobile', 'Other'] as const;

/** Non-empty phone: 10–15 digits after stripping formatting (US + international). */
export function isValidContactPhoneValue(raw: string): boolean {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 0) return false;
  return digits.length >= 10 && digits.length <= 15;
}

/** Empty OK; otherwise US ZIP or ZIP+4. */
export function isValidUsZipOptional(raw: string): boolean {
  const t = raw.trim();
  if (t === '') return true;
  return /^\d{5}(-\d{4})?$/.test(t);
}

const phoneRowSchema = z.object({
  value: z.string(),
  type: z.enum(clientContactPhoneTypeValues),
});

const contactSourceEnum = z.enum(['patient-contact', 'related-person']);

export const clientContactFormSchema = z
  .object({
    source: contactSourceEnum,
    contactKind: z.enum(CONTACT_KINDS),
    legalFirstName: z.string().trim().min(1, 'Legal first name is required'),
    legalLastName: z.string().trim().min(1, 'Legal last name is required'),
    middleName: z.string(),
    suffix: z.string(),
    preferredName: z.string(),
    relationshipType: z
      .union([z.enum(CONTACT_RELATIONSHIP_TYPES), z.literal('')])
      .refine((v) => v !== '', { message: 'Relationship type is required' }),
    isEmergencyContact: z.boolean(),
    emails: z.array(z.string()),
    phones: z.array(phoneRowSchema),
    street: z.string(),
    city: z.string(),
    state: z.string().nullable(),
    zip: z.string().refine(
      (s) => isValidUsZipOptional(s),
      { message: 'Use 5 digits or ZIP+4 (e.g. 12345 or 12345-6789)' },
    ),
    notes: z.string(),
    role: z.string(),
    organization: z.string(),
  })
  .superRefine((data, ctx) => {
    const firstPhone = data.phones[0]?.value?.trim() ?? '';
    if (firstPhone === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Phone number is required',
        path: ['phones', 0, 'value'],
      });
    }
    // Related persons are contacted through the portal, so email is their primary channel.
    if (data.contactKind === 'related-person' && (data.emails[0]?.trim() ?? '') === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Email is required for a related person',
        path: ['emails', 0],
      });
    }
    for (let i = 0; i < data.emails.length; i += 1) {
      const e = data.emails[i]?.trim() ?? '';
      if (e === '') continue;
      const r = z.string().email().safeParse(e);
      if (!r.success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Enter a valid email or leave the field empty',
          path: ['emails', i],
        });
      }
    }
    for (let i = 0; i < data.phones.length; i += 1) {
      const v = data.phones[i]?.value ?? '';
      if (v.trim() === '') continue;
      if (!isValidContactPhoneValue(v)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Enter a valid phone number (10–15 digits)',
          path: ['phones', i, 'value'],
        });
      }
    }
    if (data.relationshipType === 'Other' || data.relationshipType === 'Physician') {
      if (!data.role.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Role is required for this relationship type',
          path: ['role'],
        });
      }
    }
  });

export type ClientContactFormValues = z.input<typeof clientContactFormSchema>;

export function getDefaultClientContactFormValues(): ClientContactFormValues {
  return {
    source: 'patient-contact',
    contactKind: 'emergency-contact',
    legalFirstName: '',
    legalLastName: '',
    middleName: '',
    suffix: '',
    preferredName: '',
    relationshipType: '',
    isEmergencyContact: true,
    emails: [''],
    phones: [{ value: '', type: 'Mobile' }],
    street: '',
    city: '',
    state: null,
    zip: '',
    notes: '',
    role: '',
    organization: '',
  };
}

function contactKindFromContact(contact: Contact): ContactKind {
  if (contact.contactKind) return contact.contactKind;
  if (contact.source === 'related-person') return 'related-person';
  return 'emergency-contact';
}

export function contactToFormValues(contact: Contact): ClientContactFormValues {
  const contactKind = contactKindFromContact(contact);
  return {
    source: contact.source ?? 'patient-contact',
    contactKind,
    legalFirstName: contact.legalFirstName ?? '',
    legalLastName: contact.legalLastName ?? '',
    middleName: contact.middleName ?? '',
    suffix: contact.suffix ?? '',
    preferredName: contact.preferredName ?? '',
    relationshipType: contact.relationshipType,
    isEmergencyContact: contactKind === 'emergency-contact',
    emails: contact.emails?.length ? [...contact.emails] : [''],
    phones:
      (() => {
        const ph = contact.phones;
        if (!ph?.length) {
          return [{ value: '', type: 'Mobile' as const }];
        }
        return ph.map((p) => ({
          value: p.value ?? '',
          type: (p.type && clientContactPhoneTypeValues.includes(p.type as (typeof clientContactPhoneTypeValues)[number])
            ? p.type
            : 'Mobile') as ClientContactFormValues['phones'][number]['type'],
        }));
      })(),
    street: contact.street ?? '',
    city: contact.city ?? '',
    state: contact.state ?? null,
    zip: contact.zip ?? '',
    notes: contact.notes ?? '',
    role: contact.role ?? '',
    organization: contact.organization ?? '',
  };
}

/** Validated form → `Contact` payload for FHIR mappers (excludes `id`; caller sets when editing). */
export function clientContactFormValuesToContactPayload(values: ClientContactFormValues): Omit<Contact, 'id'> {
  const emailsTrimmed = values.emails.map((e) => e.trim()).filter(Boolean);
  const phonesFiltered = values.phones
    .map((p) => ({ value: p.value.trim(), type: p.type }))
    .filter((p) => p.value.length > 0);

  const relationshipType = values.relationshipType as ContactRelationshipType;
  const contactKind = values.contactKind as ContactKind;
  const source: ContactSource = contactKind === 'emergency-contact' ? 'patient-contact' : 'related-person';

  return {
    source,
    contactKind,
    legalFirstName: values.legalFirstName.trim(),
    legalLastName: values.legalLastName.trim(),
    middleName: values.middleName.trim() || undefined,
    suffix: values.suffix.trim() || undefined,
    preferredName: values.preferredName.trim() || undefined,
    relationshipType,
    isEmergencyContact: contactKind === 'emergency-contact',
    emails: emailsTrimmed.length > 0 ? emailsTrimmed : undefined,
    phones: phonesFiltered.length > 0 ? phonesFiltered : undefined,
    street: values.street.trim() || undefined,
    city: values.city.trim() || undefined,
    state: values.state?.trim() || undefined,
    zip: values.zip.trim() || undefined,
    notes: values.notes.trim() || undefined,
    role: values.role.trim() || undefined,
    organization: values.organization.trim() || undefined,
  };
}
